import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useApi, uploadFile, postJson } from '../api.js';
import { useCamSeen } from '../utils/plates.js';
import { bustPrefix } from '../dataCache.js';
import { fmtMoneyDecimal as fmtMoney } from '../utils/money.js';

function fmt(n) { return n == null ? '' : Number(n).toLocaleString(); }
// fmtMoney moved to utils/money.js (2026-07-03 code-review consolidation).
function fmtDate(d) {
  if (!d) return '';
  const s = String(d);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

// Pull a value from row.raw by trying each header candidate in order —
// SoilFlo header names sometimes drift slightly (trailing space, unicode,
// case). First non-empty match wins. Used for the columns we haven't
// promoted into the typed schema (Status, Bill of Lading, Approval ID,
// Net Weight, Ticket #s, etc.).
function pickRaw(row, keys) {
  const raw = row && row.raw;
  if (!raw || typeof raw !== 'object') return '';
  for (const k of keys) {
    for (const rk of Object.keys(raw)) {
      // exact match, or trimmed/case-insensitive match
      if (rk === k || rk.trim().toLowerCase() === String(k).trim().toLowerCase()) {
        const v = raw[rk];
        if (v != null && String(v).trim() !== '') return v;
      }
    }
  }
  return '';
}

// Column definitions — single source of truth for header labels, cell
// rendering, sort accessor, and filter accessor. Any new column only
// needs to be added HERE — the header row, filter row, and body row all
// pull from this array.
//   key      — stable ID (used for sort state + filter map)
//   label    — column header text
//   get      — pure function (row) => value used for filtering + sorting
//   render   — optional (row) => JSX for the cell; falls back to get()
//   className — CSS class for the cell (e.g. 'num' for right-align)
//   isNum    — true for numeric sort (compare Number(v), not string)
const COLUMNS = [
  { key: 'external_id', label: 'Load ID',
    get: r => r.external_id || '',
    render: r => <code>{r.external_id || ''}</code>,
    className: 'small' },
  { key: 'date', label: 'Date',
    get: r => r.date || '',
    render: r => fmtDate(r.date) },
  { key: 'time_at', label: 'Time',
    get: r => r.time_at || '',
    className: 'small' },
  { key: 'status', label: 'Status',
    get: r => pickRaw(r, ['Status']),
    className: 'small' },
  { key: 'material', label: 'Material',
    get: r => r.material || '' },
  { key: 'vol', label: 'Vol', isNum: true, className: 'num',
    get: r => {
      if (r.quantity != null) return r.quantity;
      const v = pickRaw(r, ['Volume']);
      return v === '' ? '' : Number(v);
    },
    render: r => {
      const v = (r.quantity != null) ? r.quantity : (pickRaw(r, ['Volume']) || '');
      return v === '' || v == null ? '' : fmt(v);
    } },
  { key: 'vol_unit', label: 'Vol unit', className: 'small',
    get: r => r.unit || pickRaw(r, ['Volume measure unit']) },
  { key: 'net_wt', label: 'Net wt', isNum: true, className: 'num',
    get: r => {
      const v = pickRaw(r, ['Net Weight', 'Net Weight (Scale)']);
      return v === '' ? '' : Number(v);
    },
    render: r => {
      const v = pickRaw(r, ['Net Weight', 'Net Weight (Scale)']);
      return v === '' ? '' : fmt(v);
    } },
  { key: 'wt_unit', label: 'Wt unit', className: 'small',
    get: r => pickRaw(r, ['Net Weight Measurement Unit', 'Weigh Scale Measurement Unit']) },
  { key: 'source_site', label: 'Source site', className: 'small',
    get: r => r.source_site || '' },
  { key: 'destination', label: 'Destination', className: 'small',
    get: r => r.destination || '' },
  { key: 'hauler', label: 'Hauler', className: 'small',
    get: r => r.hauler || '' },
  { key: 'truck', label: 'Plate', className: 'small',
    get: r => r.truck || '' },
  // "📷 verified" = gate camera read this plate on this day (rows are
  // decorated with _camVerified in displayRows; useCamSeen hook).
  { key: 'cam', label: '📷', className: 'small',
    get: r => r._camVerified ? 'verified' : (r._camProbable ? 'time' : ''),
    render: r => r._camVerified
      ? <span title="Verified: this plate was read by the gate camera on this day" style={{ color: '#15803d', fontWeight: 700 }}>✓</span>
      : r._camProbable
        ? <span title="Matched to a camera visit by other evidence (trailer, misread, operator) — see Yard visits" style={{ color: '#b45309', fontWeight: 700 }}>≈</span>
        : '' },
  // Rate-chart costing (schema-049): haulage = hauler x destination
  // rate; tip = dump-site rate (flat or per-tonne). '—' = no rate on
  // the chart for that provider/date (fix on Truck analytics > Rate
  // charts), never a silent $0.
  { key: 'haulage_cost', label: 'Haulage $', isNum: true, className: 'num',
    get: r => r.haulage_cost != null ? Number(r.haulage_cost) : '',
    render: r => r.haulage_cost != null ? '$' + Number(r.haulage_cost).toLocaleString() : <span style={{color:'#c05100'}} title="No haulage rate on the rate chart for this hauler → destination at this date">—</span> },
  { key: 'tip_fee', label: 'Tip $', isNum: true, className: 'num',
    get: r => r.tip_fee != null ? Number(r.tip_fee) : '',
    render: r => r.tip_fee != null ? '$' + Number(r.tip_fee).toLocaleString() : <span style={{color:'#c05100'}} title="No tip rate on the rate chart for this dump site at this date">—</span> },
  { key: 'total_cost', label: 'Total $', isNum: true, className: 'num',
    get: r => r.total_cost != null ? Number(r.total_cost) : '',
    render: r => r.total_cost != null ? <strong>{'$' + Number(r.total_cost).toLocaleString()}</strong> : '' },
  { key: 'driver', label: 'Driver', className: 'small',
    get: r => r.driver || '' },
  { key: 'approval_id', label: 'Approval #', className: 'small',
    get: r => pickRaw(r, ['Approval ID']) },
  { key: 'bol', label: 'BOL #', className: 'small',
    get: r => pickRaw(r, ['Bill of Lading #']) },
  { key: 'truck_ticket', label: 'Truck co ticket', className: 'small',
    get: r => pickRaw(r, ['( Outgoing Loads Form) - Truck Company Ticket # ', 'Truck Company Ticket #']) },
  { key: 'dump_ticket', label: 'Dump ticket', className: 'small',
    get: r => pickRaw(r, ['( Outgoing Loads Form) - Dump Site Ticket # ', 'Dump Site Ticket #']) },
  { key: 'cost', label: 'Cost', isNum: true, className: 'num',
    get: r => r.cost == null ? '' : Number(r.cost),
    render: r => (<span>{fmtMoney(r.cost)}{r.cost_currency ? (' ' + r.cost_currency) : ''}</span>) },
  { key: 'imported_at', label: 'Imported', className: 'small',
    get: r => r.imported_at || '',
    render: r => (<>{fmtDate(r.imported_at)} <span style={{ color: '#888' }}>by {r.imported_by || '?'}</span></>) }
];

export default function OutgoingLoads({ canWrite = false }) {
  const [from, setFrom] = useState('');
  const [to, setTo]     = useState('');
  const [q, setQ]       = useState('');
  const [limit]         = useState(500);

  // Sort state — click a header to toggle. Default sort is date desc,
  // matching what the server returned before we added client-side sort.
  const [sortBy, setSortBy]   = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  // Per-column filter values keyed by COLUMNS[i].key. Empty string = no
  // filter on that column. Case-insensitive substring match against
  // stringified accessor output.
  const [colFilters, setColFilters] = useState({});

  const qs = (() => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (from) p.set('from', from);
    if (to)   p.set('to', to);
    if (q.trim()) p.set('q', q.trim());
    return p.toString();
  })();

  const { data, error, loading, refresh } = useApi('/api/loads-outgoing?' + qs);
  // Admin-only gate for the Delete button. Suresh 2026-07-07: deletions
  // are irreversible so nobody but an admin should see the button.
  const { data: me } = useApi('/api/whoami');
  const isAdmin = !!(me && me.isAdmin);

  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [msg, setMsg] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  async function handleFilePick(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBusy(true); setMsg(null); setPreview(null);
    try {
      const result = await uploadFile('/api/loads-outgoing/import?dryRun=1', file, 'file');
      setPreview(result);
      setPendingFile(file);
    } catch (err) {
      setMsg('Preview failed: ' + err.message);
    } finally { setBusy(false); }
  }

  async function confirmImport() {
    if (!pendingFile) return;
    setBusy(true); setMsg(null);
    try {
      const result = await uploadFile('/api/loads-outgoing/import', pendingFile, 'file');
      // Differential import: `updated` counts rows that matched an existing
      // external_id and got their missing fields filled in. New response shape
      // (2026-07-04) — old `skippedDuplicate` is gone.
      const parts = [];
      if (result.inserted) parts.push(result.inserted + ' new row' + (result.inserted === 1 ? '' : 's'));
      if (result.updated)  parts.push(result.updated  + ' existing row' + (result.updated  === 1 ? '' : 's') + ' merged');
      setMsg('Imported: ' + (parts.length ? parts.join(', ') : 'nothing changed') + '.');
      setPreview(null);
      setPendingFile(null);
      if (fileRef.current) fileRef.current.value = '';
      // Bust EVERY cached /api/loads-outgoing URL (different tabs use
      // different date ranges / limits). Without this, the Overview
      // outgoing tiles keep showing stale numbers until the user
      // hard-refreshes. refresh() alone only re-fetches this component's
      // own URL, not the Overview panel's.
      bustPrefix('/api/loads-outgoing');
      refresh();
    } catch (err) {
      setMsg('Import failed: ' + err.message);
    } finally { setBusy(false); }
  }

  async function deleteRow(id) {
    if (!confirm('Delete this load? This is permanent.')) return;
    setBusy(true);
    try {
      const r = await fetch('/api/loads-outgoing/' + id, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error(r.status + ' ' + (await r.text()));
      bustPrefix('/api/loads-outgoing');
      refresh();
    } catch (err) {
      setMsg('Delete failed: ' + err.message);
    } finally { setBusy(false); }
  }

  // Rules of Hooks: run useMemo BEFORE any conditional early return, so React
  // sees the same hook count on every render regardless of `loading` state.
  // Previously this crashed with error #310 whenever loading toggled (e.g.
  // debounced search triggered a fetch). See CONTEXT.md 2026-07-04 modal
  // saga for the same class of bug.
  const rows = (data && data.rows) || [];
  const total = (data && data.total) || 0;

  // Camera-verified marks: fetch seen plates for the rows' date span.
  const dateSpan = useMemo(() => {
    if (!rows.length) return { lo: null, hi: null };
    let lo = null, hi = null;
    for (const r of rows) {
      const d = String(r.date || '').slice(0, 10);
      if (!d) continue;
      if (!lo || d < lo) lo = d;
      if (!hi || d > hi) hi = d;
    }
    return { lo, hi };
  }, [rows]);
  const { isSeen, isProbable } = useCamSeen(dateSpan.lo, dateSpan.hi);

  const displayRows = useMemo(() => {
    let rs = rows.map(r => {
      const v = isSeen(r.date, r.truck);
      return { ...r, _camVerified: v, _camProbable: !v && isProbable(r.date, r.truck, r.id) };
    });
    // Column filters
    for (const c of COLUMNS) {
      const f = (colFilters[c.key] || '').toLowerCase().trim();
      if (!f) continue;
      rs = rs.filter(r => String(c.get(r) ?? '').toLowerCase().includes(f));
    }
    // Sort
    const col = COLUMNS.find(c => c.key === sortBy);
    if (col) {
      const dir = sortDir === 'asc' ? 1 : -1;
      rs.sort((a, b) => {
        const av = col.get(a);
        const bv = col.get(b);
        if (col.isNum) {
          const na = av === '' || av == null ? -Infinity : Number(av);
          const nb = bv === '' || bv == null ? -Infinity : Number(bv);
          if (na === nb) return 0;
          return dir * (na < nb ? -1 : 1);
        }
        return dir * String(av || '').localeCompare(String(bv || ''));
      });
    }
    return rs;
  }, [rows, colFilters, sortBy, sortDir, isSeen, isProbable]);

  if (loading && !data) return <div className="loading">Loading outgoing loads…</div>;

  // Header click handler: 3-state cycle per column. First click = ascending,
  // second click on the same column = descending, third click = clear the sort
  // and fall back to date desc (the default).
  function toggleSort(key) {
    if (sortBy !== key) { setSortBy(key); setSortDir('asc'); return; }
    if (sortDir === 'asc')  { setSortDir('desc'); return; }
    setSortBy('date'); setSortDir('desc');
  }

  const anyColFilter = Object.values(colFilters).some(v => v && v.trim());

  return (
    <div className="page">
      <div className="page-head">
        <h1>Outgoing loads <span className="small">— {fmt(total)} loads in view{from || to || q ? ' (filtered)' : ''}{anyColFilter ? ' · column filters active' : ''}</span></h1>
        <div className="page-head-controls">
          <button className="refresh-btn" onClick={refresh} disabled={busy}>Reload</button>
          {canWrite && (
            <>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm"
                     onChange={handleFilePick} style={{ display: 'none' }} />
              <button className="refresh-btn" onClick={() => fileRef.current && fileRef.current.click()}
                      disabled={busy} style={{ background: '#15803d' }}>
                📥 Import CSV / Excel
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: '0.5rem 1rem', marginBottom: '0.75rem',
                                     display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        <label className="small">From <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
        <label className="small">To <input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
        <input type="text" value={q} onChange={e => setQ(e.target.value)}
               placeholder="Search material, hauler, truck, driver, source, destination…"
               style={{ flex: 1, minWidth: '15em', padding: '4px 8px' }} />
        {(from || to || q || anyColFilter) && (
          <button className="refresh-btn small" onClick={() => { setFrom(''); setTo(''); setQ(''); setColFilters({}); }} style={{ background: '#94a3b8' }}>Clear all</button>
        )}
      </div>

      {error && <div className="error" style={{ marginBottom: '0.5rem' }}>Error: {error}</div>}
      {msg && <div className="card" style={{ padding: '0.5rem 0.8rem', marginBottom: '0.5rem',
                                              background: msg.startsWith('Imported') ? '#dcfce7' : '#fee2e2' }}>{msg}</div>}

      {/* Import preview modal */}
      {preview && (
        <div className="modal-backdrop" onClick={() => !busy && setPreview(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ minWidth: 540, maxWidth: 800 }}>
            <h3 style={{ marginTop: 0 }}>Preview — {preview.filename}</h3>
            <div className="small" style={{ marginBottom: '0.5rem' }}>
              {preview.totalRowsInFile} rows in file · <strong>{preview.validRowsToImport}</strong> valid to import
            </div>
            <details style={{ marginBottom: '0.5rem' }}>
              <summary className="small">Column matches ({preview.columnMatches.length} mapped)</summary>
              <table className="datatable" style={{ fontSize: '0.85em', margin: '0.4rem 0' }}>
                <thead><tr><th>Field</th><th>Source column</th></tr></thead>
                <tbody>
                  {preview.columnMatches.map(m => (
                    <tr key={m.field}><td><code>{m.field}</code></td><td>{m.column}</td></tr>
                  ))}
                </tbody>
              </table>
              <div className="small" style={{ color: '#666' }}>
                Unmatched headers are preserved verbatim in <code>raw</code> JSONB.
              </div>
            </details>
            <h4 style={{ marginBottom: '0.3rem' }}>First 5 rows</h4>
            <div style={{ maxHeight: '20em', overflowY: 'auto', fontSize: '0.82em' }}>
              <table className="datatable">
                <thead><tr>
                  <th>Date</th><th>Material</th><th className="num">Qty</th><th>Unit</th><th>Source → Dest</th><th>Hauler / Truck</th>
                </tr></thead>
                <tbody>
                  {preview.preview.map((r, i) => (
                    <tr key={i}>
                      <td>{fmtDate(r.date)}</td>
                      <td>{r.material || ''}</td>
                      <td className="num">{fmt(r.quantity)}</td>
                      <td>{r.unit || ''}</td>
                      <td className="small">{(r.source_site || '?')} → {(r.destination || '?')}</td>
                      <td className="small">{(r.hauler || '?') + ' / ' + (r.truck || '?')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', justifyContent: 'flex-end' }}>
              <button className="refresh-btn" onClick={() => { setPreview(null); setPendingFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                      disabled={busy} style={{ background: '#94a3b8' }}>Cancel</button>
              <button className="refresh-btn" onClick={confirmImport}
                      disabled={busy || preview.validRowsToImport === 0} style={{ background: '#0369a1' }}>
                {busy ? 'Importing…' : 'Import ' + preview.validRowsToImport + ' rows'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="chart-card" style={{ padding: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="datatable" style={{ width: '100%' }}>
            <thead className="sticky-thead">
              {/* Sort row — click header to cycle asc → desc → default. */}
              <tr>
                {COLUMNS.map(c => {
                  const active = sortBy === c.key;
                  const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
                  return (
                    <th key={c.key} className={c.className} onClick={() => toggleSort(c.key)}
                        style={{ cursor: 'pointer', userSelect: 'none',
                                 background: active ? '#eff6ff' : undefined,
                                 whiteSpace: 'nowrap' }}
                        title="Click to sort">
                      {c.label}{arrow}
                    </th>
                  );
                })}
                {isAdmin && <th></th>}
              </tr>
              {/* Filter row — one small text input per column, case-
                  insensitive substring match against the column's accessor. */}
              <tr>
                {COLUMNS.map(c => (
                  <th key={c.key + '-f'} className={c.className} style={{ padding: '2px 4px', background: '#f8fafc' }}>
                    <input type="text" value={colFilters[c.key] || ''}
                           onChange={e => setColFilters(f => ({ ...f, [c.key]: e.target.value }))}
                           placeholder="filter…"
                           style={{ width: '100%', fontSize: '0.78em', padding: '2px 4px',
                                    border: '1px solid #cbd5e1', borderRadius: 3,
                                    background: (colFilters[c.key] || '').trim() ? '#fef3c7' : '#fff' }} />
                  </th>
                ))}
                {isAdmin && <th style={{ background: '#f8fafc' }}></th>}
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 && (
                <tr><td colSpan={isAdmin ? COLUMNS.length + 1 : COLUMNS.length} style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>
                  {rows.length === 0
                    ? (canWrite ? 'No outgoing loads yet. Use the 📥 Import button to upload a CSV or Excel from SoilFlo.' : 'No outgoing loads yet.')
                    : 'No rows match the current column filters.'}
                </td></tr>
              )}
              {displayRows.map(r => (
                <tr key={r.id}>
                  {COLUMNS.map(c => (
                    <td key={c.key} className={c.className}>
                      {c.render ? c.render(r) : (c.get(r) || '')}
                    </td>
                  ))}
                  {isAdmin && (
                    <td><button className="refresh-btn small" style={{ background: '#b91c1c' }}
                                onClick={() => deleteRow(r.id)} disabled={busy} title="Delete row (admin only)">×</button></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && data.rows && data.rows.length === data.limit && (
          <div className="small" style={{ padding: '0.5rem 1rem', color: '#666' }}>
            Showing first {data.limit} loads. Narrow the date range to see more.
          </div>
        )}
      </div>
    </div>
  );
}
